import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnInit, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';

export interface LayoutOption {
  id: string;
  label: string;
  icon: string;
  tooltip: string;
}

export interface SearchOptions {
  term: string;
  filterMode: boolean;
}

@Component({
  selector: 'app-layout-switcher',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatMenuModule, MatTooltipModule, FormsModule],
  templateUrl: './layout-switcher.component.html',
  styleUrls: ['./layout-switcher.component.css']
})
export class LayoutSwitcherComponent implements OnInit, OnChanges {
  @Input() isCollapsed = false;
  @Input() isPaneOne_Collapsed = false;
  @Input() isPaneTwo_Collapsed = false;
  @Input() showSingleTree = false;
  @Input() hideSearch = false;
  @Input() isMobileView = false;

  @Output() layoutChange = new EventEmitter<string>();
  @Output() searchTermChange = new EventEmitter<SearchOptions>();

  @ViewChild('searchInput') searchInput!: ElementRef;

  layoutOptions: LayoutOption[] = [
    {
      id: 'single_tree',
      label: 'Single Tree',
      icon: 'account_tree',
      tooltip: 'Show full tree in single pane'
    }
  ];

  currentLayout = 'single_tree';
  showSearch = false;
  searchTerm = '';
  filterMode = false;
  searchTimeout: any = null;

  ngOnInit(): void {
    // Initialize component
    console.log('LayoutSwitcher initialized with showSingleTree:', this.showSingleTree);
    // Always start in single tree mode
    if (!this.showSingleTree) {
      setTimeout(() => {
        this.selectLayout('single_tree');
      }, 0);
    }
  }

  // Add ngOnChanges to detect when showSingleTree input changes
  ngOnChanges(changes: any): void {
    // If we've switched to single tree mode and this was triggered by a search action
    // automatically open the search box
    if (changes.showSingleTree && changes.showSingleTree.currentValue === true) {
      console.log('LayoutSwitcher: Detected switch to single tree mode');

      // We know we got here from a search action if we have a lastSearchAction timestamp
      if (this._lastSearchActionTime && (Date.now() - this._lastSearchActionTime) < 500) {
        console.log('LayoutSwitcher: Opening search box after layout change');
        this.showSearch = true;
        this.focusSearchInput();
        // Clear the timestamp so we don't re-open search on other changes
        this._lastSearchActionTime = 0;
      }
    }
  }

  // Track when search was requested
  private _lastSearchActionTime = 0;

  constructor() {}

  selectLayout(layoutId: string): void {
    // In mobile view, toggle the mobile navigation instead of selecting a layout
    if (this.isMobileView) {
      // Emit a special event to toggle mobile navigation
      this.layoutChange.emit('toggle_mobile_nav');
    } else {
      // Always use single_tree layout
      this.currentLayout = 'single_tree';
      this.layoutChange.emit('single_tree');
    }
  }

  getActiveIcon(): string {
    // Only single tree layout is available
    return 'account_tree';
  }

  getActiveTooltip(): string {
    return 'Single tree view';
  }

  // Search functionality
  toggleSearch(): void {
    // Record the time when search is toggled
    this._lastSearchActionTime = Date.now();

    // If not already showing search and not already in single tree mode,
    // switch to single tree mode first
    if (!this.showSearch && !this.showSingleTree) {
      console.log('LayoutSwitcher: Switching to single tree mode for search');
      this.layoutChange.emit('single_tree');

      // Note: We don't need to set showSearch here any more as ngOnChanges will handle it
      // when the parent component updates the showSingleTree property
    } else {
      // Already in proper mode, just toggle search visibility
      this.showSearch = !this.showSearch;

      if (this.showSearch) {
        this.focusSearchInput();
      } else {
        // Clear search when closing
        this.clearSearch();
      }
    }
  }

  // Helper method to focus the search input
  private focusSearchInput(): void {
    // Focus the search input after it's displayed
    setTimeout(() => {
      if (this.searchInput) {
        this.searchInput.nativeElement.focus();
      }
    }, 0);
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.searchTermChange.emit({ term: '', filterMode: this.filterMode });
  }

  toggleFilterMode(): void {
    this.filterMode = !this.filterMode;
    console.log(`Filter mode ${this.filterMode ? 'enabled' : 'disabled'}`);

    // Record the time of the search-related action
    this._lastSearchActionTime = Date.now();

    // Make sure we're in single tree mode when toggling filter
    if (!this.showSingleTree) {
      console.log('LayoutSwitcher: Switching to single tree mode for filtering');
      this.layoutChange.emit('single_tree');
    } else {
      // Already in single tree mode, just apply search with new filter
      if (this.searchTerm.trim()) {
        this.performSearch();
      }
    }
  }

  onSearchKeyUp(event: KeyboardEvent): void {
    // Don't trigger search on Enter key as it has its own handler
    if (event.key === 'Enter') {
      return;
    }

    // Clear existing timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // Set new timeout for search (500ms delay)
    this.searchTimeout = setTimeout(() => {
      this.performSearch();
    }, 500);
  }

  performSearch(): void {
    // Clear any pending timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    console.log('LayoutSwitcher: performSearch() called with term:', this.searchTerm, 'filterMode:', this.filterMode);

    // Record the time of the search action
    this._lastSearchActionTime = Date.now();

    // Make sure we're in single tree mode when performing search
    if (!this.showSingleTree) {
      console.log('LayoutSwitcher: Switching to single tree mode for search');
      this.layoutChange.emit('single_tree');
    } else {
      // Already in single tree mode, just emit search
      // Only emit if there's a search term
      if (this.searchTerm.trim()) {
        console.log('LayoutSwitcher: Emitting searchTermChange event with term:', this.searchTerm.trim());
        this.searchTermChange.emit({
          term: this.searchTerm.trim(),
          filterMode: this.filterMode
        });
      } else {
        // Clear search if term is empty
        this.clearSearch();
      }
    }
  }
}
